import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Send,
  Trash2,
  Plus,
  X,
  CheckCircle2,
  Circle,
  Clock,
  MessageSquare,
  ArrowUpCircle,
  ArrowRightCircle,
  ArrowDownCircle,
  ListChecks,
  Globe,
} from "lucide-react";
import { useTaskDetail } from "@/hooks/useTaskBoard";
import { useTaskBoard } from "@/hooks/useTaskBoard";
import { useAuth } from "@/hooks/useAuth";
import { useWebsites } from "@/hooks/useWebsites";
import { useToast } from "@/hooks/use-toast";
import { useDisplayNames } from "@/hooks/useUserProfile";

interface TaskDetailProps {
  taskId: string;
  onBack: () => void;
}

const statusFlow = {
  open: { label: "Otwarte", next: "in_progress" as const, nextLabel: "Rozpocznij", icon: Circle, color: "text-blue-500", bg: "bg-blue-50 text-blue-700" },
  in_progress: { label: "W trakcie", next: "done" as const, nextLabel: "Zakoncz", icon: Clock, color: "text-orange-500", bg: "bg-orange-50 text-orange-700" },
  done: { label: "Zrobione", next: "open" as const, nextLabel: "Otworz ponownie", icon: CheckCircle2, color: "text-green-500", bg: "bg-green-50 text-green-700" },
};

const priorityConfig = {
  high: { label: "Wysoki", icon: ArrowUpCircle, color: "text-red-500", bg: "bg-red-50 text-red-700 border-red-200" },
  medium: { label: "Sredni", icon: ArrowRightCircle, color: "text-yellow-500", bg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  low: { label: "Niski", icon: ArrowDownCircle, color: "text-blue-400", bg: "bg-blue-50 text-blue-700 border-blue-200" },
};

export default function TaskDetail({ taskId, onBack }: TaskDetailProps) {
  const {
    task,
    items,
    comments,
    loading,
    toggleItem,
    addItem,
    removeItem,
    addComment,
    deleteComment,
  } = useTaskDetail(taskId);
  const { updateTaskStatus, updateTaskPriority, deleteTask } = useTaskBoard();
  const { user } = useAuth();
  const { websites } = useWebsites();
  const { toast } = useToast();
  const { getDisplayName, getInitial } = useDisplayNames();

  const websiteName = task?.website_id
    ? websites.find((w) => w.id === task.website_id)?.name || null
    : null;

  const [newItemContent, setNewItemContent] = useState("");
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const isOwner = task?.user_id === user?.id;

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length]);

  const handleAddItem = async () => {
    if (!newItemContent.trim()) return;
    const { error } = await addItem(newItemContent.trim());
    if (error) toast({ title: "Blad", description: error, variant: "destructive" });
    else setNewItemContent("");
  };

  const handleItemKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSendingComment(true);
    const { error } = await addComment(newComment.trim());
    if (error) toast({ title: "Blad", description: error, variant: "destructive" });
    else setNewComment("");
    setSendingComment(false);
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleStatusChange = async () => {
    if (!task) return;
    const next = statusFlow[task.status].next;
    const { error } = await updateTaskStatus(taskId, next);
    if (error) toast({ title: "Blad", description: error, variant: "destructive" });
  };

  const handlePriorityChange = async (priority: "low" | "medium" | "high") => {
    const { error } = await updateTaskPriority(taskId, priority);
    if (error) toast({ title: "Blad", description: error, variant: "destructive" });
  };

  const handleDelete = async () => {
    const { error } = await deleteTask(taskId);
    if (error) toast({ title: "Blad", description: error, variant: "destructive" });
    else {
      toast({ title: "Task usuniety" });
      onBack();
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3" />
        <div className="h-32 bg-muted rounded" />
        <div className="h-24 bg-muted rounded" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Task nie znaleziony</p>
        <Button variant="ghost" onClick={onBack} className="mt-2">
          Wroc do listy
        </Button>
      </div>
    );
  }

  const sConfig = statusFlow[task.status];
  const pConfig = priorityConfig[task.priority];
  const StatusIcon = sConfig.icon;
  const PriorityIcon = pConfig.icon;
  const doneCount = items.filter((i) => i.is_checked).length;
  const progress = items.length > 0 ? Math.round((doneCount / items.length) * 100) : null;

  return (
    <div className="space-y-4">
      {/* Naglowek */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={onBack} className="mt-0.5 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h2 className={`font-semibold text-xl ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>
              {task.title}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Utworzono: {formatDate(task.created_at)} · Aktualizacja: {formatDate(task.updated_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Usunac task?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Ta operacja jest nieodwracalna. Wszystkie pozycje i komentarze zostana usuniete.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Usun</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Status + Priorytet */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={handleStatusChange} className="gap-1.5">
          <StatusIcon className={`h-4 w-4 ${sConfig.color}`} />
          {sConfig.nextLabel}
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex gap-1">
          {(["high", "medium", "low"] as const).map((p) => {
            const pc = priorityConfig[p];
            const Icon = pc.icon;
            return (
              <button
                key={p}
                onClick={() => handlePriorityChange(p)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all border ${
                  task.priority === p
                    ? `${pc.bg} ring-1 ring-offset-1 ring-primary/20`
                    : "border-transparent hover:border-border"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${pc.color}`} />
                {pc.label}
              </button>
            );
          })}
        </div>

        <Badge variant="outline" className={`${sConfig.bg} border-0`}>
          {sConfig.label}
        </Badge>

        {websiteName && (
          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 gap-1">
            <Globe className="h-3 w-3" />
            {websiteName}
          </Badge>
        )}
      </div>

      {/* Opis */}
      {task.description && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm whitespace-pre-wrap">{task.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Checklista */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ListChecks className="h-4 w-4" />
              Checklista
              {items.length > 0 && (
                <span className="text-xs text-muted-foreground font-normal">
                  ({doneCount}/{items.length})
                </span>
              )}
            </CardTitle>
          </div>
          {progress !== null && (
            <Progress value={progress} className="h-2 mt-2" />
          )}
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 group transition-colors ${
                  item.is_checked ? "bg-muted/30" : "hover:bg-muted/50"
                }`}
              >
                <Checkbox
                  checked={item.is_checked}
                  onCheckedChange={() => toggleItem(item.id, item.is_checked)}
                />
                <span
                  className={`flex-1 text-sm ${
                    item.is_checked ? "line-through text-muted-foreground" : ""
                  }`}
                >
                  {item.content}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  onClick={() => removeItem(item.id)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>

          {/* Dodaj nowa pozycje */}
          <div className="flex gap-2 mt-3">
            <Input
              placeholder="Dodaj pozycje..."
              value={newItemContent}
              onChange={(e) => setNewItemContent(e.target.value)}
              onKeyDown={handleItemKeyDown}
              className="text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleAddItem}
              disabled={!newItemContent.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Komentarze */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Komentarze
            {comments.length > 0 && (
              <span className="text-xs text-muted-foreground font-normal">
                ({comments.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {comments.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Brak komentarzy. Napisz pierwszy!
            </p>
          ) : (
            <div className="space-y-3 mb-4">
              {comments.map((comment) => {
                const isMe = comment.user_id === user?.id;
                return (
                  <div key={comment.id} className="flex gap-3 group">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                        isMe
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {getInitial(comment.user_id)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium">
                          {getDisplayName(comment.user_id)}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {formatDate(comment.created_at)}
                        </span>
                        {isMe && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                            onClick={() => deleteComment(comment.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <div className="text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-2.5">
                        {comment.content}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={commentsEndRef} />
            </div>
          )}

          {/* Nowy komentarz */}
          <div className="flex gap-2">
            <Textarea
              placeholder="Napisz komentarz... (Ctrl+Enter)"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleCommentKeyDown}
              className="min-h-[60px] resize-none text-sm"
            />
            <Button
              onClick={handleAddComment}
              disabled={!newComment.trim() || sendingComment}
              className="shrink-0 self-end"
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
